import { RatingBoard, RatingHistory, RatingRevisionType } from '@/enums/rating.enum';
import type { GlobalRatingBoardType } from '@/types/rating/rating.type';

export const SUPPORT_RATING_TYPE_HISTORY = [
  RatingHistory.RATING_VERIFY_PASS,
  RatingHistory.RATING_VERIFY_REJECT,
  RatingHistory.RATING_REVISION_PASS,
  RatingHistory.RATING_REVISION_REJECT,
  RatingHistory.RATING_RECLASSIFICATION,
  RatingHistory.RATING_RECLASSIFICATION_COMMITTEE,
  RatingHistory.RATING_CANCELED,
  RatingHistory.RATING_REJECTED,
  RatingHistory.DIRECT_RATING_REGISTER,
  RatingHistory.DIRECT_RATING_MODIFY,
  RatingHistory.GRAC_RATING_REGISTER,
  RatingHistory.RATING_RECLASSIFICATION_ADJUSTED,
  RatingHistory.RATING_RECLASSIFICATION_COMMITTEE_ADJUSTED,
  RatingHistory.RATING_CANCEL_ADJUSTED,
  RatingHistory.RATING_REJECT_ADJUSTED,
  RatingHistory.GLOBAL_RATING_MODIFY,
  RatingHistory.GLOBAL_RATING_REGISTER,
  RatingHistory.GLOBAL_RATING_DELETE,
  RatingHistory.BUG_FIX_REVISION,
  RatingHistory.GRAC_RATING_DELETE,
  RatingHistory.RATING_DELETE,
  RatingHistory.RATING_OBJECTION_PASS,
  RatingHistory.RATING_OBJECTION_REJECT
];

export const RATING_RESULT_TAB = {
  NONE: 'NONE', // Use for show guide screen
  OBJECTION: 'OBJECTION',
  OBJECTION_IN_PROGRESS: 'OBJECTION_IN_PROGRESS',
  OBJECTION_COMPLETE: 'OBJECTION_COMPLETE',
  OBJECTION_REJECTION: 'OBJECTION_REJECTION',
  DIRECT_INPUT_COMPLETE: 'DIRECT_INPUT_COMPLETE',
  ADJUSTMENT: 'ADJUSTMENT',
  RECLASSIFICATION: 'RECLASSIFICATION',
  CANCEL: 'CANCEL',
  REJECTION: 'REJECTION',
  HISTORY: 'HISTORY',
  PRE_RELEASE: 'PRE_RELEASE',
  SAVE: 'SAVE',
  VERIFYING: 'VERIFYING',
  REQUEST_ACCEPTED: 'REQUEST_ACCEPTED',
  VERIFY_REJECTED: 'VERIFY_REJECTED',
  MODIFICATION_VERIFYING: 'MODIFICATION_VERIFYING',
  MODIFICATION_COMPLETE: 'MODIFICATION_COMPLETE',
  MODIFICATION_REJECTION: 'MODIFICATION_REJECTION'
} as const;
export type RatingResultTab = keyof typeof RATING_RESULT_TAB;

export const RATING_RESULT_DETAIL_TAB = {
  ADJUSTMENT: 'ADJUSTMENT',
  RECLASSIFICATION: 'RECLASSIFICATION',
  CANCEL: 'CANCEL',
  REJECTION: 'REJECTION'
};
export type RatingResultDetailTab = keyof typeof RATING_RESULT_DETAIL_TAB;

// Global Rating Options
export const ACB_RATING_OPTIONS = {
  G: '0', // ALL
  PG: 'PG', // ALL
  M: 'M', // ALL
  'MA15+': '15', // 15+
  'R18+': '18' // 18+
} as const;

export type AcbRatingOptionValue = (typeof ACB_RATING_OPTIONS)[keyof typeof ACB_RATING_OPTIONS];

export const CERO_RATING_OPTIONS = {
  A: '0', // ALL
  B: '12', // 12+
  C: '15', // 15+
  D: '17', // 17+
  Z: '18' // 18+
} as const;

export type CeroRatingOptionValue = (typeof CERO_RATING_OPTIONS)[keyof typeof CERO_RATING_OPTIONS];

export const CLASSIND_RATING_OPTIONS = {
  L: '0', // ALL
  10: '10', // 10+
  12: '12', // 12+
  14: '14', // 14+
  16: '16', // 16+
  18: '18' // 18+
} as const;

export type ClassIndRatingOptionValue =
  (typeof CLASSIND_RATING_OPTIONS)[keyof typeof CLASSIND_RATING_OPTIONS];

export const ESRB_RATING_OPTIONS = {
  EVERYONE: '0', // ALL
  'EVERYONE 10+': '10', // 10+
  TEEN: '13', // 13+
  'MATURE 17+': '17', // 17+
  'ADULTS ONLY 18+': '18' // 18+
} as const;

export type EsrbRatingOptionValue = (typeof ESRB_RATING_OPTIONS)[keyof typeof ESRB_RATING_OPTIONS];

export const IARC_RATING_OPTIONS = {
  'Ages 3+': '3', // 3+
  'Ages 7+': '7', // 7+
  'Ages 12+': '12', // 12+
  'Ages 16+': '16', // 16+
  'Ages 18+': '18' // 18+
} as const;

export type IarcRatingOptionValue = (typeof IARC_RATING_OPTIONS)[keyof typeof IARC_RATING_OPTIONS];

export const PEGI1_RATING_OPTIONS = {
  'PEGI 3': '3', // 3+
  'PEGI 7': '7', // 7+
  'PEGI 12': '12', // 12+
  'PEGI 16': '16', // 16+
  'PEGI 18': '18' // 18+
} as const;

export type Pegi1RatingOptionValue =
  (typeof PEGI1_RATING_OPTIONS)[keyof typeof PEGI1_RATING_OPTIONS];

export const PEGI2_RATING_OPTIONS = {
  'PEGI 4': '4', // 4+
  'PEGI 6': '6', // 6+
  'PEGI 12': '12', // 12+
  'PEGI 16': '16', // 16+
  'PEGI 18': '18' // 18+
} as const;

export type Pegi2RatingOptionValue =
  (typeof PEGI2_RATING_OPTIONS)[keyof typeof PEGI2_RATING_OPTIONS];

export const USK_RATING_OPTIONS = {
  'USK 0': '0', // ALL
  'USK 6': '6', // 6+'
  'USK 12': '12', // 12+
  'USK 16': '16', // 16+
  'USK 18': '18' // 18+
} as const;

export type UskRatingOptionValue = (typeof USK_RATING_OPTIONS)[keyof typeof USK_RATING_OPTIONS];

export type GlobalRatingOptionValue =
  | AcbRatingOptionValue
  | CeroRatingOptionValue
  | ClassIndRatingOptionValue
  | EsrbRatingOptionValue
  | IarcRatingOptionValue
  | Pegi1RatingOptionValue
  | Pegi2RatingOptionValue
  | UskRatingOptionValue;

export const GLOBAL_RATING_OPTIONS = {
  [RatingBoard.Acb]: ACB_RATING_OPTIONS,
  [RatingBoard.Cero]: CERO_RATING_OPTIONS,
  [RatingBoard.ClassInd]: CLASSIND_RATING_OPTIONS,
  [RatingBoard.Esrb]: ESRB_RATING_OPTIONS,
  [RatingBoard.Iarc]: IARC_RATING_OPTIONS,
  [RatingBoard.Pegi1]: PEGI1_RATING_OPTIONS,
  [RatingBoard.Pegi2]: PEGI2_RATING_OPTIONS,
  [RatingBoard.Usk]: USK_RATING_OPTIONS
} as const;

export type GlobalRatingOption = (typeof GLOBAL_RATING_OPTIONS)[keyof typeof GLOBAL_RATING_OPTIONS];

export const GLOBAL_RATING_BOARDS: GlobalRatingBoardType[] = [
  {
    boardName: RatingBoard.Esrb,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Esrb],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.ESRB'
  },
  {
    boardName: RatingBoard.Pegi1,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Pegi1],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.PEGI'
  },
  {
    boardName: RatingBoard.Pegi2,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Pegi2],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.PEGI2'
  },
  {
    boardName: RatingBoard.Usk,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Usk],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.USK'
  },
  {
    boardName: RatingBoard.Acb,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Acb],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.ACB'
  },
  {
    boardName: RatingBoard.ClassInd,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.ClassInd],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.CLASS_IND'
  },
  {
    boardName: RatingBoard.Cero,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Cero],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.CERO'
  },
  {
    boardName: RatingBoard.Iarc,
    ratingOption: GLOBAL_RATING_OPTIONS[RatingBoard.Iarc],
    locale: 'studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.intl_org.IARC'
  }
];
// End Global Rating Options

export const GRAC_RADIO = {
  SELF_REVIEW: 'SELF_REVIEW',
  DIRECT_INPUT: 'DIRECT_INPUT'
} as const;

export type GracRadioType = keyof typeof GRAC_RADIO;

export const PLANING_RATTING = {
  AGE_0: '0',
  AGE_12: '12',
  AGE_15: '15',
  AGE_19: '19',
  REJECT: 'REJECT',
  CANCEL: 'CANCEL'
};

export const LINE_RADIO = [
  '1.1.1.1.',
  '1.1.2.1.',
  '1.1.3.1.',
  '1.1.3.2.',
  '1.2.1.1.',
  '1.3.1.1.',
  '1.4.1.1.',
  '1.5.1.1.',
  '2.1.1.1.',
  '2.1.2.1.',
  '2.2.1.1.',
  '2.3.1.1.',
  '3.1.1.1.',
  '3.2.1.1.',
  '3.2.2.1.',
  '4.1.1.1.',
  '4.2.1.1.',
  '5.1.1.1.',
  '6.1.1.1.',
  '7.1.1.1.'
];

export const SURVEY_NEXT = {
  END: 'END'
};

export const RATING_REVISION_TYPE_TO_TRANSLATION = {
  [RatingRevisionType.OTHERS]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_other',
  [RatingRevisionType.BUG_FIX]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.bug_fix',
  [RatingRevisionType.CHANGED_CONTENTS_3]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_background',
  [RatingRevisionType.CHANGED_CONTENTS_4]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_sys_ui',
  [RatingRevisionType.MODIFIED_BILLING_SYSTEM]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_billing_sys',
  [RatingRevisionType.CHANGED_CONTENTS_2]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_mission_scenario',
  [RatingRevisionType.CHANGED_CONTENTS_1]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_char_item',
  [RatingRevisionType.CHANGED_GENRE]:
    'studio.prj_prod_mngmt.rating_build_review.self_review.report_change.changed_info_genre'
} as const;
